import { Inject, Injectable, OnDestroy, Optional } from '@angular/core'
import { Store } from '@ngrx/store'
import { Actions, ofType } from '@ngrx/effects'
import { CommentaryEntityContext } from '@helios/common-api'
import {
  RootState,
  commentaryAccountCountsLoaded,
  commentaryAccountCountsSettled,
  commentaryCountsLoadFailed,
  commentaryCountsSettled,
  loadCommentaryAccountCounts,
  loadCommentaryCounts,
  selectCommentaryAccountCounts,
  selectCommentaryCountsByAccount,
  selectCommentaryUnavailableAccountIds,
  clearCommentaryCounts,
} from '@helios/state-management'
import { Observable, ReplaySubject, Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { CommentaryCountDimension } from '../types/commentary-count-dimension'

/**
 * DLITE-5094: outstanding counts loads per account. Loads of one account
 * can overlap (a forced reload dispatches while another runs, and the
 * effects use mergeMap), so this is a count, not a flag: an account stays in
 * flight until every outstanding request has had its outcome.
 */
class InFlightLoads {
  private readonly outstanding = new Map<string, number>()

  has(id: string): boolean {
    return (this.outstanding.get(id) ?? 0) > 0
  }

  add(id: string): void {
    this.outstanding.set(id, (this.outstanding.get(id) ?? 0) + 1)
  }

  /** One outstanding request for `id` has had its outcome. */
  delete(id: string): void {
    const left = (this.outstanding.get(id) ?? 0) - 1
    if (left > 0) this.outstanding.set(id, left)
    else this.outstanding.delete(id)
  }

  ids(): string[] {
    return [...this.outstanding.keys()]
  }

  clear(): void {
    this.outstanding.clear()
  }
}

/**
 * Sync facade over the `commentaryCounts` NgRx slice.
 * Mirrors counts into private fields so AG Grid's synchronous `valueGetter`
 * can read the current count without subscribing to an Observable.
 * `changes$` (replay-1) lets grids call `refreshCells` after each update.
 */
@Injectable({ providedIn: 'root' })
export class CommentaryCountsService implements OnDestroy {
  // Within-account entity counts, keyed by accountId → entity map. Per-account
  // so shared keys (e.g. asset:<security>) don't collide across accounts.
  private countsByAccount: Record<string, Record<string, number>> = {}
  // DLITE-4112: per-account totals for the account-list grids, keyed by plain
  // accountId. Accumulates as grids load/scroll.
  private accountLatest: Record<string, number> = {}
  // DLITE-5094: accounts whose commentary the user cannot read.
  private unavailable: Record<string, true> = {}
  // Accounts whose per-account TOTAL was already requested (account-list scroll).
  private readonly requestedAccountIds = new Set<string>()
  // Accounts whose full entity map is currently being fetched (entity grids).
  // Not a persistent "already loaded" set — presence in `countsByAccount` is the
  // loaded-marker, so a store clear (session change) naturally allows a reload.
  // DLITE-5094: counted per request (see InFlightLoads).
  private readonly inFlightEntityAccountIds = new InFlightLoads()
  // DLITE-5094: accounts whose cached entity map may be out of date because no
  // entity surface was showing them (see markStale).
  // Maps each stale account to the ordering stamp of its newest invalidation.
  private readonly staleEntityAccountIds = new Map<string, number>()
  // DLITE-5094: accountId → ordering stamp of its newest entity load dispatch.
  // The effects apply only the newest request's result, so a landing map is
  // the outcome of this dispatch; it satisfies a stale mark only if it was
  // dispatched after that mark. Without this, a load already in flight when
  // the account went stale would clear the mark with pre-invalidation counts.
  private readonly latestEntityDispatch = new Map<string, number>()
  // Monotonic stamp shared by invalidations and dispatches, for ordering.
  private entityStamp = 0
  // DLITE-5094: accounts whose per-account TOTAL request is still running
  // (between its dispatch and its loaded/failed/settled action).
  // `requestedAccountIds` alone can't tell "in flight" from "loaded". Only
  // tracked when the Actions stream is available to clear it.
  private readonly inFlightAccountTotalIds = new InFlightLoads()
  // DLITE-5094: load actions this service dispatched (and already counted),
  // so the Actions observer counts only loads dispatched by someone else.
  private readonly ownLoadActions = new WeakSet()
  private readonly destroyed$ = new Subject<void>()
  private readonly changesSubject = new ReplaySubject<void>(1)

  /** Observable that emits every time the counts mirror updates. */
  readonly changes$: Observable<void> = this.changesSubject.asObservable()

  constructor(
    private readonly store: Store<RootState>,
    // Optional so components under test don't need EffectsModule wired just to
    // instantiate the service; production always has the Actions stream.
    // Explicit @Inject token because a `Actions | null` union can't be inferred
    // as a DI token from the reflected type.
    @Optional() @Inject(Actions) private readonly actions$: Actions | null,
  ) {
    this.store
      .select(selectCommentaryCountsByAccount)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((countsByAccount: Record<string, Record<string, number>>) => {
        const previous = this.countsByAccount
        this.countsByAccount = countsByAccount ?? {}
        // DLITE-5094: a fresh map landing for a stale account makes it current
        // again only when its request was dispatched after the account went
        // stale; an older in-flight load's result keeps the mark, so the next
        // ensure call refetches. A removed map (store clear) needs no mark.
        for (const [id, staleAt] of [...this.staleEntityAccountIds]) {
          if (this.countsByAccount[id] === previous[id]) continue
          const dispatchedAt = this.latestEntityDispatch.get(id)
          if (
            !this.countsByAccount[id] ||
            dispatchedAt === undefined ||
            dispatchedAt > staleAt
          ) {
            this.staleEntityAccountIds.delete(id)
          }
        }
        // A freshly landed map clears the account's in-flight marker; a store
        // clear (empty map) drops all markers so the grids reload for the new
        // context. "Freshly landed" is a new map reference, not mere presence:
        // a stale account refetching still holds its old map, and an update
        // to another account must not end its fetch early (DLITE-5094). Each
        // fresh map is one request's outcome, so it ends one outstanding load.
        if (Object.keys(this.countsByAccount).length === 0) {
          this.inFlightEntityAccountIds.clear()
          this.latestEntityDispatch.clear()
        }
        for (const id of this.inFlightEntityAccountIds.ids()) {
          if (
            this.countsByAccount[id] &&
            this.countsByAccount[id] !== previous[id]
          ) {
            this.inFlightEntityAccountIds.delete(id)
          }
        }
        this.changesSubject.next()
      })

    this.store
      .select(selectCommentaryAccountCounts)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((counts: Record<string, number>) => {
        this.accountLatest = counts ?? {}
        this.changesSubject.next()
      })

    // DLITE-5094: accounts the user cannot read render as "unavailable".
    this.store
      .select(selectCommentaryUnavailableAccountIds)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((unavailable: Record<string, true> | undefined) => {
        const next = unavailable ?? {}
        if (next === this.unavailable) return
        this.unavailable = next
        this.changesSubject.next()
      })

    // DLITE-4110 review #2: a failed fetch must not wedge an account. On
    // success the countsByAccount mirror above clears the in-flight marker, but
    // a failure never touches that map (only sets error), so the entity marker
    // would stick forever and the account-total request would stay "requested"
    // pegged at 0. Drop BOTH dedup markers here so the next grid modelUpdated
    // retries instead of leaving a permanent silent 0.
    // DLITE-5094: drop only the markers of the surface whose request failed
    // (`source`), so a failed entity GET can't end a still-running bulk
    // totals request's tracking (or vice versa) and invite a duplicate
    // request. An unknown source drops both, as before.
    this.actions$
      ?.pipe(ofType(commentaryCountsLoadFailed), takeUntil(this.destroyed$))
      .subscribe(({ payload }) => {
        if (payload.source !== 'bulk') {
          this.inFlightEntityAccountIds.delete(payload.accountId)
        }
        if (payload.source !== 'single') {
          this.requestedAccountIds.delete(payload.accountId)
          this.inFlightAccountTotalIds.delete(payload.accountId)
        }
        this.changesSubject.next()
      })

    this.actions$
      ?.pipe(ofType(commentaryAccountCountsLoaded), takeUntil(this.destroyed$))
      .subscribe(({ payload }) => {
        Object.keys(payload?.counts ?? {}).forEach(id =>
          this.inFlightAccountTotalIds.delete(id),
        )
      })

    // DLITE-5094: a counts load dispatched by anyone else (the drawer's
    // commentary-threads-changed fallback, any direct dispatch) is in flight
    // too, so a recovery or grid ensure* call while it runs is not sent again.
    // The markers clear on the same loaded/failed/settled outcomes as above.
    // Loads this service dispatched itself were counted before dispatch.
    this.actions$
      ?.pipe(
        ofType(loadCommentaryCounts, loadCommentaryAccountCounts),
        takeUntil(this.destroyed$),
      )
      .subscribe(action => {
        if (this.ownLoadActions.has(action)) return
        if (action.type === loadCommentaryCounts.type) {
          const { accountId } = (
            action as ReturnType<typeof loadCommentaryCounts>
          ).payload
          this.trackEntityLoad(accountId)
          return
        }
        this.trackTotalsLoad(
          (action as ReturnType<typeof loadCommentaryAccountCounts>).payload
            ?.accountIds ?? [],
        )
      })

    // A bulk request that ended with no loaded/failed outcome (404) is no
    // longer in flight; its requested markers stay, so grids still stop asking.
    this.actions$
      ?.pipe(ofType(commentaryAccountCountsSettled), takeUntil(this.destroyed$))
      .subscribe(({ payload }) => {
        ;(payload?.accountIds ?? []).forEach(id =>
          this.inFlightAccountTotalIds.delete(id),
        )
      })

    // An entity request whose result was superseded by a newer request for
    // the same account (the effect drops it) is no longer in flight either.
    this.actions$
      ?.pipe(ofType(commentaryCountsSettled), takeUntil(this.destroyed$))
      .subscribe(({ payload }) => {
        if (payload?.accountId) {
          this.inFlightEntityAccountIds.delete(payload.accountId)
        }
      })
  }

  ngOnDestroy(): void {
    this.destroyed$.next()
    this.destroyed$.complete()
    this.changesSubject.complete()
  }

  /** Force a (re)load of one account's full entity-count map. */
  loadFor(accountId: string): void {
    if (!accountId) return
    this.store.dispatch(loadCommentaryCounts({ accountId }))
  }

  /**
   * DLITE-4112: ensure per-account TOTALS are loaded for the given accounts
   * (the rows currently loaded in an Accounts / My Accounts grid). Skips
   * accounts already requested this session so scrolling doesn't refetch.
   */
  ensureAccountsLoaded(accountIds: (string | number)[]): void {
    // DLITE-5094: a request still in flight is skipped too, even when a failure
    // of an overlapping request has dropped the account's requested marker.
    const toLoad = [
      ...new Set(accountIds.map(id => String(id)).filter(id => !!id)),
    ].filter(
      id =>
        !this.requestedAccountIds.has(id) &&
        !this.inFlightAccountTotalIds.has(id),
    )
    if (toLoad.length === 0) return
    this.trackTotalsLoad(toLoad)
    this.dispatchOwnLoad(loadCommentaryAccountCounts({ accountIds: toLoad }))
  }

  /**
   * DLITE-4112: ensure the full entity-count map is loaded for each account
   * whose entity rows are displayed (Transactions / Positions / Breaks) — for
   * the row's own account, so counts work with or without a session and across
   * multi-account / sleeve-aggregate sessions. Deduped so repeated grid model
   * updates don't refetch.
   */
  ensureEntityCountsLoaded(accountIds: (string | number)[]): void {
    const unique = new Set(accountIds.map(id => String(id)).filter(id => !!id))
    unique.forEach(accountId => {
      // Skip if already loaded (present in the store, and not marked stale by
      // the live service) or a fetch is in flight that will satisfy it. A
      // fetch dispatched before the account went stale does not: its result
      // may predate the invalidation, so a fresh one is sent (DLITE-5094).
      const staleAt = this.staleEntityAccountIds.get(accountId)
      if (
        (this.countsByAccount[accountId] && staleAt === undefined) ||
        (this.inFlightEntityAccountIds.has(accountId) &&
          !this.inFlightPredates(accountId, staleAt))
      ) {
        return
      }
      // A stale mark stays until the fresh map lands (the mirror clears it),
      // so a failed refetch leaves the account stale and the next call retries.
      this.trackEntityLoad(accountId)
      this.dispatchOwnLoad(loadCommentaryCounts({ accountId }))
    })
  }

  /**
   * DLITE-5094: `accountId`'s cached counts for `surface` may be out of date
   * (no surface of that kind is showing it, so the live socket's reloads skip
   * it). The next `ensure*` call for it fetches once instead of trusting the
   * cache.
   */
  markStale(accountId: string, surface: 'entity' | 'total'): void {
    if (!accountId) return
    if (surface === 'total') this.requestedAccountIds.delete(accountId)
    else this.staleEntityAccountIds.set(accountId, ++this.entityStamp)
  }

  /** True when the newest entity load of `accountId` was dispatched before
   *  its stale mark `staleAt` (so that load cannot clear the mark). */
  private inFlightPredates(accountId: string, staleAt: number | undefined) {
    if (staleAt === undefined) return false
    const dispatchedAt = this.latestEntityDispatch.get(accountId)
    return dispatchedAt !== undefined && dispatchedAt < staleAt
  }

  /**
   * DLITE-5094: reload `accountIds` for `surface` through the same dedup the
   * grids use, unless a load of the account is already running (that load's
   * result is as fresh as a new one). The live service's recovery path for
   * accounts marked unavailable, so recovery never duplicates a grid's own
   * load.
   */
  refreshUnlessLoading(
    accountIds: (string | number)[],
    surface: 'entity' | 'total',
  ): void {
    const ids = accountIds.map(id => String(id)).filter(id => !!id)
    if (surface === 'entity') {
      ids.forEach(id => {
        if (!this.inFlightEntityAccountIds.has(id)) {
          this.staleEntityAccountIds.set(id, ++this.entityStamp)
        }
      })
      this.ensureEntityCountsLoaded(ids)
      return
    }
    ids.forEach(id => {
      if (!this.inFlightAccountTotalIds.has(id)) {
        this.requestedAccountIds.delete(id)
      }
    })
    this.ensureAccountsLoaded(ids)
  }

  /**
   * DLITE-5094: forced reload of `accountIds` for `surface` — a thread event,
   * a reconnect or the drawer fallback, where the cached result (and any load
   * already running) may predate the change, so it always dispatches. It is
   * recorded in the same in-flight markers `ensure*` / `refreshUnlessLoading`
   * check, so a recovery or grid load while it runs is not sent again.
   */
  forceReload(
    accountIds: (string | number)[],
    surface: 'entity' | 'total',
  ): void {
    const ids = [
      ...new Set(accountIds.map(id => String(id)).filter(id => !!id)),
    ]
    if (ids.length === 0) return
    if (surface === 'entity') {
      ids.forEach(accountId => {
        this.trackEntityLoad(accountId)
        this.dispatchOwnLoad(loadCommentaryCounts({ accountId }))
      })
      return
    }
    this.trackTotalsLoad(ids)
    this.dispatchOwnLoad(loadCommentaryAccountCounts({ accountIds: ids }))
  }

  /** Dispatch a load this service has already counted as in flight. */
  private dispatchOwnLoad(
    action:
      | ReturnType<typeof loadCommentaryCounts>
      | ReturnType<typeof loadCommentaryAccountCounts>,
  ): void {
    this.ownLoadActions.add(action)
    this.store.dispatch(action)
  }

  /** An entity load for `accountId` is running (cleared when its fresh map
   *  lands, on its failure, or on a store clear). */
  private trackEntityLoad(accountId: string): void {
    if (!accountId) return
    const id = String(accountId)
    this.inFlightEntityAccountIds.add(id)
    this.latestEntityDispatch.set(id, ++this.entityStamp)
  }

  /** A totals load for `accountIds` is running: requested (its result is as
   *  fresh as a new one), and in flight while an outcome can clear it. One
   *  request per id, as the effect gives each id of a request one outcome. */
  private trackTotalsLoad(accountIds: (string | number)[]): void {
    new Set(accountIds.map(raw => String(raw))).forEach(id => {
      if (!id) return
      this.requestedAccountIds.add(id)
      if (this.actions$) this.inFlightAccountTotalIds.add(id)
    })
  }

  /**
   * Clears the within-account ENTITY maps only (`clearCommentaryCounts` empties
   * `countsByAccount`; the mirror subscription then drops the in-flight entity
   * markers). It deliberately does NOT reset the account-list TOTALS
   * (`accountCounts`) or their dedup set (`requestedAccountIds`) — those persist
   * for the tab's lifetime by design, and `requestedAccountIds` is only pruned
   * per-account on a load failure. So a caller wanting a hard reset of the
   * per-account totals can't get it from here; that would need a dedicated
   * reset. (`clear()` currently has no production callers — kept for parity.)
   */
  clear(): void {
    this.store.dispatch(clearCommentaryCounts())
  }

  /** DLITE-5094: true when the user cannot read this account's commentary
   *  (socket denial, or a 403 / bulk-failed counts load). */
  isUnavailable(accountId: string | number | null | undefined): boolean {
    if (accountId === null || accountId === undefined || accountId === '') {
      return false
    }
    return this.unavailable[String(accountId)] === true
  }

  countForRow(
    row: CommentaryEntityContext | null | undefined,
    dimension: CommentaryCountDimension,
  ): number {
    if (!row) return 0
    const accountId = row.accountId != null ? String(row.accountId) : null
    if (!accountId) return 0
    return dimension.countIn(
      {
        accountId,
        byLinkKey: this.countsByAccount[accountId] ?? {},
        total: this.accountLatest[accountId] ?? 0,
      },
      row,
    )
  }
}
